import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  AdminPageHeader,
  Button,
  ButtonStyles,
  ButtonTypes,
  ButtonSize,
  Card,
  Input,
  InputTypes,
  Modal,
  MLPVersion,
  Theme,
  RadioButton,
  TableContainer,
} from '../../../../ui/components';
import { LoggerService , dateTimeToCustomFormat, printPDF} from '../../../../ui/utils';
import AuditLogService from '../../../../sdk/services/auditLogService';
import AuditHistoryAccordion from '../../audit-log/auditLogAccordion';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../../hooks';
import RedemptionService from '../../../../sdk/services/redemptionService';
import { getRewardListConfig, RedemptionData } from '../redemptionConfig';
import styles from './index.module.scss';

interface AdminProps {
  adminType: number;
}

export const AdminGeneralRedemptionEdit = ({ adminType }: AdminProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');
  const [redemptionData, setRedemptionData] = useState<RedemptionData | null>(null);
  const [initialStatus, setInitialStatus] = useState('');
  const [invalid, setInvalid] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [auditHistoryLog, setAuditHistoryLog] = useState([]);
  const [displayAuditEmpty, setAuditDisplayEmpty] = useState(false);

  const apiCall = useAPICall();
  const params = new URLSearchParams(location.search);
  const campaignId = params.get('campaignId') ?? '';
  const isUnauthorized = adminType !== null && ![1].includes(adminType);

  
  // Use useEffect to update redemptionData when id changes
useEffect(() => {
  if (!campaignId || !id) return;

  const fetchData = async () => {
    try {
      const redemptionPayload = {
        orderNumber: id,
        campaignId,
      };

      const auditPayload = {
        campaignId,
        ref: id,
      };

      const [
        redemptionResponse,
        historyLogResponse,
      ] = await Promise.all([
        apiCall(RedemptionService.getRedemptionByOrder, redemptionPayload),
        apiCall(AuditLogService.fetchLog, auditPayload),
      ]);

      const redemptionData = redemptionResponse?.data?.data ?? {};
      setRedemptionData(redemptionData);
      setInitialStatus(redemptionData.status);

      const historyLogResult = historyLogResponse.data;
      const auditLogs = historyLogResult?.data || [];
      setAuditHistoryLog(auditLogs);
      setAuditDisplayEmpty(auditLogs.length === 0);

    } catch (error) {
      LoggerService.log('Failed to retrieve redemption detail or audit log', error);
    }
  };

  fetchData();
}, [campaignId, id]);



  const handleInputChange = (name: string, value: DynamicObjectInterface) => {

    if (name === 'status'){
      setInvalid(redemptionData?.status === value ? true : false);
      //setStatus(value);
      setShowDisclaimer(value === 'CANCELLED');
    }

    setRedemptionData(prevRedemptionData => ({
      ...prevRedemptionData!,
      [name]: value,
    }));

  };

  const groupedRewards =
      redemptionData?.rewards.reduce((acc, r) => {
        const existing = acc.find(item => item.rewardCode === r.rewardCode);

        if (existing) {
          existing.quantity += 1;
        } else {
          acc.push({
            rewardCode: r.rewardCode,
            rewardName: r.rewardName,
            quantity: 1,
          });
        }

        return acc;
      }, [] as { rewardCode: string; rewardName: string; quantity: number }[]) || [];
 
  const handlePrint = () => {
    const start = dateTimeToCustomFormat(redemptionData?.campaignStart, 'DD MMM YYYY');
    const end = dateTimeToCustomFormat(redemptionData?.campaignEnd, 'DD MMM YYYY');
    const headers = [t('admin.redemption.invoice.header') + redemptionData?.campaignName, 
      t('admin.redemption.invoice.header2') + start + ' - ' + end]
    const lines = [t('admin.redemption.invoice.orderingABO') +  redemptionData?.orderAboId, 
      t('admin.redemption.invoice.volumeToABO') +  redemptionData?.volumeAboId, 
      t('admin.redemption.invoice.orderNumber') +  redemptionData?.orderNumber, ]
    const columns = [t('admin.redemption.invoice.number'), t('admin.redemption.invoice.sku'), t('admin.redemption.invoice.reward'), t('admin.redemption.invoice.quantity')];
    const rows = groupedRewards?.map((r, i) => [
      i + 1,
      r.rewardCode,
      r.rewardName,
      r.quantity,
    ]);
    printPDF(
      headers,
      lines,
      columns,
      redemptionData?.status === 'CANCELLED',
      rows,
      { size: "half-A4-horizontal", orientation: "l" }
    );
  };

  const updateRedemptionStatus = async () => {

    const payload = {
      status: redemptionData?.status,
      campaignId: redemptionData?.campaignId,
      orderNumber: redemptionData?.orderNumber,
      recIds: redemptionData?.rewards.map(r => r.recId),
      locationCode: redemptionData?.locationCode,
      rewards: groupedRewards
    };

    const result = await apiCall(RedemptionService.updateRedemption, payload);
    if (result.status === 200 && result?.data?.status) {
      navigate(`/admin/promotion/manage-redemption/list?campaignId=${campaignId}`);
      adminToast({
        message: result?.data?.message,
        theme: Theme.SUCCESS,
      });
    } else {
      adminToast({
        message: result?.data?.message,
        theme: Theme.ERROR,
      });
    }
  }
  
  const onSave = async () => {
    if (redemptionData?.status === 'CANCELLED'){
      setShowDelete(true);
    }else{
      setShowDelete(false);
      await updateRedemptionStatus();
    }
  };

  const buttonConfig = [
    {
      title: t('admin.redemption.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/promotion/manage-redemption/list?campaignId=${campaignId}`);
      },
    },
    {
      title: t('admin.redemption.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        onSave();
      },
    },
  ];

  const rewardBtnConfig = [
    {
      title: t('admin.redemption.print'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        handlePrint();
      },
    },
  ];

  // const fetchRewardList = async () => {
  //   try {
  //     const result = await apiCall(RewardService.getRewardList);
  //     const rewards = result?.data?.data?.map((reward: DynamicObjectInterface) => ({
  //       id: reward.rewardCode,
  //       name: reward.rewardName,
  //     }));
  //     setRewardList(rewards);
  //     // Handle the reward list data as needed
  //   } catch (error) {
  //     LoggerService.log('Failed to retrieve reward list', error);
  //     // Handle error appropriately
  //   }
  // };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.redemption.cancelMessage')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.redemption.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                updateRedemptionStatus();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.redemption.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader
        title={t('admin.redemption.editTitle')}
        button={buttonConfig}
      />
      <div className={styles['edit-redemption-form-container']}>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled
            type={InputTypes.TEXT}
            name={'campaignId'}
            label={`${t('admin.redemption.campaignId')}`}
            value={campaignId}
            mlpVersion={MLPVersion.ONE}
          />
        </div>
        <div/>
        <div className={styles['input-container']}>
           <Input
            key={id}
            disabled
            type={InputTypes.TEXT}
            name={'orderNumber'}
            label={`${t('admin.redemption.orderNumber')}`}
            value={id || ''}
            mlpVersion={MLPVersion.ONE}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled
            type={InputTypes.TEXT}
            name={'locationName'}
            label={`${t('admin.redemption.locationName')}`}
            value={redemptionData?.locationName}
            mlpVersion={MLPVersion.ONE}
          />
        </div>
        <div className={styles['input-container']}>
          <div className={styles.inputWrapper}>
            <Input
              key={id}
              disabled={true}
              type={InputTypes.NUMBER}
              name="orderAboId"
              label={`${t('admin.redemption.orderAboId')}`}
              value={redemptionData?.orderAboId}
              mlpVersion={MLPVersion.ONE}
              onChange={(e) => handleInputChange('orderAboId', e.target.value)}
            />
          </div>
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={true}
            type={InputTypes.NUMBER}
            name={'volumeAboId'}
            label={`${t('admin.redemption.volumeAboId')}`}
            value={redemptionData?.volumeAboId ?? 0}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('volumeAboId', e.target.value);
            }}
          />
        </div>

         <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={true}
            type={InputTypes.NUMBER}
            name={'channel'}
            label={`${t('admin.redemption.channel')}`}
            value={redemptionData?.channel ?? '-'}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('channel', e.target.value);
            }}
          />
        </div>

        <div className={styles['input-container']}>
          <div className={styles['input']}>
            <div className={styles['input-label']}>{`${t('admin.redemption.status')}*`}</div>
            <div className={styles['radio-button-container']}>
              <RadioButton
                label="SELECTED"
                checked={redemptionData?.status === 'SELECTED'}
                disabled={initialStatus === 'CANCELLED'}
                onClick={() => {
                  handleInputChange('status', 'SELECTED');
                }}
              />
              <RadioButton
                label="DISTRIBUTED"
                checked={redemptionData?.status === 'DISTRIBUTED'}
                disabled={initialStatus === 'CANCELLED'}
                onClick={() => {
                  handleInputChange('status', 'DISTRIBUTED');
                }}
              />
              { !isUnauthorized && (
                <RadioButton
                  label="CANCELLED"
                  checked={redemptionData?.status === 'CANCELLED'}
                  disabled={initialStatus === 'CANCELLED'}
                  onClick={() => {
                    handleInputChange('status', 'CANCELLED');
                }}
              />
              )}

            </div>
            { showDisclaimer && (
                <div style={{color:'red'}}>{t('admin.redemption.disclaimer')}</div>
            )}
          </div>
        </div>
      </div>
      {deletePrompt}
      <Card title={t('admin.redemption.rewards') + `  (${redemptionData?.rewards.length||0}/${redemptionData?.chances||0})`} button={rewardBtnConfig}>
        <TableContainer dataSource={redemptionData?.rewards||[]} getColumns={getRewardListConfig} />
      </Card>
      <AuditHistoryAccordion auditHistoryLog={auditHistoryLog} displayAuditEmpty={displayAuditEmpty} />
    </div>
    
  );
};
